import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';

/**
 * Cross-platform QR code download utility
 * Works on both web and native Capacitor platforms
 */

export interface QRCodeDownloadOptions {
  fileName?: string;
  padding?: number;
  size?: number;
}

/**
 * Convert SVG element to PNG data URL
 */
export const convertSvgToPng = (svg: SVGElement, options: QRCodeDownloadOptions = {}): Promise<string> => {
  const { padding = 20, size } = options;
  
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement("canvas");
      const svgData = new XMLSerializer().serializeToString(svg);
      const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      
      const img = new Image();
      img.onload = () => {
        // Set canvas dimensions with padding
        const finalSize = size || img.width;
        canvas.width = finalSize + (padding * 2);
        canvas.height = finalSize + (padding * 2);
        const ctx = canvas.getContext("2d");
        
        if (ctx) {
          // Draw white background
          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Draw the QR code centered with padding
          ctx.drawImage(img, padding, padding, finalSize, finalSize);
          
          // Convert to PNG data URL
          const pngUrl = canvas.toDataURL("image/png");
          URL.revokeObjectURL(url);
          resolve(pngUrl);
        } else {
          reject(new Error("Could not get canvas context"));
        }
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Failed to load SVG image"));
      };
      
      img.src = url;
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Download QR code on native platform using Capacitor Filesystem
 */
export const downloadQRCodeNative = async (dataUrl: string, fileName: string) => {
  // Extract base64 data from data URL
  const base64Data = dataUrl.split(',')[1];
  const timestampedFileName = `${fileName}-${Date.now()}.png`;
  
  await Filesystem.writeFile({
    path: timestampedFileName,
    data: base64Data,
    directory: Directory.Documents,
    encoding: Encoding.UTF8
  });
};

/**
 * Download QR code on web platform using traditional download
 */
export const downloadQRCodeWeb = (dataUrl: string, fileName: string) => {
  const downloadLink = document.createElement("a");
  downloadLink.href = dataUrl;
  downloadLink.download = `${fileName}.png`;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
};

/**
 * Main function to download QR code across platforms
 */
export const downloadQRCode = async (
  svgElement: SVGElement, 
  fileName: string,
  options: QRCodeDownloadOptions = {}
): Promise<void> => {
  // Convert SVG to PNG data URL
  const pngDataUrl = await convertSvgToPng(svgElement, options);
  
  if (Capacitor.isNativePlatform()) {
    // Native platform: use Capacitor Filesystem
    await downloadQRCodeNative(pngDataUrl, fileName);
  } else {
    // Web platform: use traditional download
    downloadQRCodeWeb(pngDataUrl, fileName);
  }
};
